import { useState, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import { toast } from 'react-hot-toast'
import { Plus, Pencil, Trash2, Filter, Search, ChevronDown, ChevronUp, Loader2, Tag, Zap, Palette, Calendar, Hash, Settings, FileText, Briefcase, CreditCard, Receipt, Calculator } from 'lucide-react'
import {
  Card,
  CardContent,
  Button,
  Input,
  Label,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Badge,
  Textarea,
  Separator,
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
  Switch,
} from '@jira-clone/ui'
import { useDocumentNumbering, useDocumentNumberingMutations } from '../../shared/hooks/useDocumentNumbering'
import { getApiErrorMessage } from '../../shared/utils/apiError'
import { currentFiscalYear, fiscalYearOptions, normalizeFiscalYear } from '../../lib/fiscalYear'

const DOC_TYPES = [
  { key: 'quotation', label: 'Quotation', prefix: 'QT', icon: FileText },
  { key: 'proforma', label: 'Proforma Invoice', prefix: 'PI', icon: Briefcase },
  { key: 'taxInvoice', label: 'Tax Invoice', prefix: 'TI', icon: CreditCard },
  { key: 'runningBill', label: 'Running Bill', prefix: 'RB', icon: Receipt },
  { key: 'estimateInvoice', label: 'Estimate Invoice', prefix: 'EI', icon: Calculator },
] as const

type DocTypeKey = (typeof DOC_TYPES)[number]['key']

interface DocNumberingConfig {
  prefix: string
  fiscalYear: string
  startingNumber: number
  currentCounter: number
  numberFormat: string
  padding: number
  isActive: boolean
}

interface DraftState {
  id?: string
  name: string
  quotation: DocNumberingConfig
  proforma: DocNumberingConfig
  taxInvoice: DocNumberingConfig
  runningBill: DocNumberingConfig
  estimateInvoice: DocNumberingConfig
  isActive: boolean
}

const createDefaultConfig = (prefix: string): DocNumberingConfig => ({
  prefix,
  fiscalYear: currentFiscalYear(),
  startingNumber: 1,
  currentCounter: 0,
  numberFormat: 'DT-{PREFIX}-{FY}/{NUMBER}',
  padding: 4,
  isActive: true,
})

// Running bills (stage bills on a milestone proforma) read DT/RB/2026-27/001.
const createDefaultRunningBillConfig = (): DocNumberingConfig => ({
  ...createDefaultConfig('RB'),
  numberFormat: 'DT/{PREFIX}/{FY}/{NUMBER}',
  padding: 3,
})

const EMPTY_DRAFT: DraftState = {
  name: '',
  quotation: createDefaultConfig('QT'),
  proforma: createDefaultConfig('PI'),
  taxInvoice: createDefaultConfig('TI'),
  runningBill: createDefaultRunningBillConfig(),
  estimateInvoice: createDefaultConfig('EI'),
  isActive: true,
}

function resetCounter(setDraft: React.Dispatch<React.SetStateAction<DraftState>>, docType: keyof Omit<DraftState, 'id' | 'name' | 'isActive'>) {
  setDraft((d) => ({
    ...d,
    [docType]: { ...d[docType], currentCounter: 0 }
  }))
}

export default function DocumentNumberingAdminPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const [search, setSearch] = useState(searchParams.get('search') || '')
  const [isActiveFilter, setIsActiveFilter] = useState<'all' | 'true' | 'false'>(searchParams.get('isActive') as any || 'all')

  const handleSearchChange = (value: string) => {
    setSearch(value)
    setSearchParams(value ? { search: value } : {})
  }

  const handleIsActiveChange = (value: 'all' | 'true' | 'false') => {
    setIsActiveFilter(value)
    setSearchParams(value === 'all' ? {} : { isActive: value })
  }

  const { data: configs = [], isLoading } = useDocumentNumbering({
    isActive: isActiveFilter === 'all' ? undefined : isActiveFilter === 'true',
    search: search || undefined,
  })
  
  const { create, update, setStatus, remove } = useDocumentNumberingMutations()

  const [dialogOpen, setDialogOpen] = useState(false)
  const [draft, setDraft] = useState<DraftState>(EMPTY_DRAFT)
  const [deletingId, setDeletingId] = useState<string | null>(null)
  const [activeTab, setActiveTab] = useState<DocTypeKey>('quotation')

  const openCreate = () => {
    setDraft(EMPTY_DRAFT)
    setActiveTab('quotation')
    setDialogOpen(true)
  }

  const openEdit = (item: any) => {
    // Configs saved before the short form ("2026-27") hold "2026-2027"; show and save them in the short form.
    const withShortYear = (config: DocNumberingConfig | undefined, fallbackPrefix: string): DocNumberingConfig =>
      config ? { ...config, fiscalYear: normalizeFiscalYear(config.fiscalYear) } : createDefaultConfig(fallbackPrefix)
    setDraft({
      id: item.id,
      name: item.name,
      quotation: withShortYear(item.quotation, 'QT'),
      proforma: withShortYear(item.proforma, 'PI'),
      taxInvoice: withShortYear(item.taxInvoice, 'TI'),
      runningBill: item.runningBill ? withShortYear(item.runningBill, 'RB') : createDefaultRunningBillConfig(),
      estimateInvoice: withShortYear(item.estimateInvoice, 'EI'),
      isActive: item.isActive,
    })
    setActiveTab('quotation')
    setDialogOpen(true)
  }

  const handleSave = async () => {
    if (!draft.name.trim()) {
      toast.error('Name is required.')
      return
    }
    const sanitizeFormat = (fmt?: string) => {
      let f = fmt?.trim() || 'DT-{PREFIX}-{FY}/{NUMBER}'
      if (!f.includes('{NUMBER}')) {
        const match = f.match(/([-_/])\d+$/)
        if (match && match.index !== undefined) {
          f = `${f.slice(0, match.index)}${match[1]}{NUMBER}`
        } else {
          f = `${f.replace(/[-/]$/, '')}/{NUMBER}`
        }
      }
      return f
    }

    try {
      const payload = {
        name: draft.name.trim(),
        quotation: { ...draft.quotation, numberFormat: sanitizeFormat(draft.quotation.numberFormat) },
        proforma: { ...draft.proforma, numberFormat: sanitizeFormat(draft.proforma.numberFormat) },
        taxInvoice: { ...draft.taxInvoice, numberFormat: sanitizeFormat(draft.taxInvoice.numberFormat) },
        runningBill: { ...draft.runningBill, numberFormat: sanitizeFormat(draft.runningBill.numberFormat) },
        estimateInvoice: { ...draft.estimateInvoice, numberFormat: sanitizeFormat(draft.estimateInvoice.numberFormat) },
        isActive: draft.isActive,
      }
      if (draft.id) {
        await update.mutateAsync({ id: draft.id, payload })
        toast.success('Document numbering updated.')
      } else {
        await create.mutateAsync(payload)
        toast.success('Document numbering created.')
      }
      setDialogOpen(false)
    } catch (err) {
      toast.error(getApiErrorMessage(err, draft.id ? 'update document numbering' : 'create document numbering'))
    }
  }

  const handleDelete = async (item: any) => {
    if (!confirm(`Delete "${item.name}"? This can't be undone.`)) return
    setDeletingId(item.id)
    try {
      await remove.mutateAsync(item.id)
      toast.success('Document numbering deleted.')
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'delete document numbering'))
    } finally {
      setDeletingId(null)
    }
  }

  const handleToggleActive = async (item: any) => {
    try {
      await setStatus.mutateAsync({ id: item.id, isActive: !item.isActive })
      toast.success(item.isActive ? 'Numbering deactivated.' : 'Numbering activated.')
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'toggle numbering status'))
    }
  }

  const previewNumber = (config: DocNumberingConfig) => {
    const nextNum = (config.currentCounter + 1).toString().padStart(config.padding, '0')
    const fy = normalizeFiscalYear(config.fiscalYear)
    let format = config.numberFormat || 'DT-{PREFIX}-{FY}/{NUMBER}'
    if (!format.includes('{NUMBER}')) {
      const match = format.match(/([-_/])\d+$/)
      if (match && match.index !== undefined) {
        format = `${format.slice(0, match.index)}${match[1]}{NUMBER}`
      } else {
        format = `${format.replace(/[-/]$/, '')}/{NUMBER}`
      }
    }
    let prefix = config.prefix
    if (format.includes('DT-{PREFIX}') && prefix.startsWith('DT-')) {
      format = format.replace('DT-{PREFIX}', '{PREFIX}')
    }
    return format
      .replace('{PREFIX}', prefix)
      .replace('{FY}', fy)
      .replace('{NUMBER}', nextNum)
  }

  const renderConfigFields = (docType: keyof Omit<DraftState, 'id' | 'name' | 'isActive'>) => {
    const config = draft[docType]
    const updateField = <K extends keyof DocNumberingConfig>(field: K, val: DocNumberingConfig[K]) => {
      setDraft((d) => ({
        ...d,
        [docType]: {
          ...d[docType],
          [field]: val,
        },
      }))
    }

    return (
      <div className="space-y-4 p-4">
        <div className="grid gap-4 md:grid-cols-2">
          <div className="space-y-1.5">
            <Label htmlFor={`${docType}-prefix`}>Prefix</Label>
            <Input
              id={`${docType}-prefix`}
              value={config.prefix}
              onChange={(e) => updateField('prefix', e.target.value.toUpperCase())}
              placeholder="QT"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor={`${docType}-fy`}>Fiscal Year</Label>
            <Select value={config.fiscalYear} onValueChange={(v) => updateField('fiscalYear', v)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {fiscalYearOptions().map((fy) => (
                  <SelectItem key={fy} value={fy}>{fy}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor={`${docType}-start`}>Starting Number</Label>
            <Input
              id={`${docType}-start`}
              type="number"
              min={1}
              value={config.startingNumber}
              onChange={(e) => updateField('startingNumber', parseInt(e.target.value) || 1)}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor={`${docType}-padding`}>Number Padding</Label>
            <Input
              id={`${docType}-padding`}
              type="number"
              min={1}
              max={10}
              value={config.padding}
              onChange={(e) => updateField('padding', parseInt(e.target.value) || 4)}
            />
          </div>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor={`${docType}-format`}>Number Format</Label>
          <Input
            id={`${docType}-format`}
            value={config.numberFormat}
            onChange={(e) => updateField('numberFormat', e.target.value)}
            placeholder="DT-{PREFIX}-{FY}/{NUMBER}"
          />
          <div className="flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground pt-1">
            <span>Click to add token:</span>
            <button
              type="button"
              className="px-1.5 py-0.5 bg-muted hover:bg-muted/80 rounded border font-mono text-[11px]"
              onClick={() => updateField('numberFormat', `${config.numberFormat}{PREFIX}`)}
            >{'{PREFIX}'}</button>
            <button
              type="button"
              className="px-1.5 py-0.5 bg-muted hover:bg-muted/80 rounded border font-mono text-[11px]"
              onClick={() => updateField('numberFormat', `${config.numberFormat}{FY}`)}
            >{'{FY}'}</button>
            <button
              type="button"
              className="px-1.5 py-0.5 bg-muted hover:bg-muted/80 rounded border font-mono text-[11px]"
              onClick={() => updateField('numberFormat', `${config.numberFormat}/{NUMBER}`)}
            >{'{NUMBER}'}</button>
          </div>
          {!config.numberFormat?.includes('{NUMBER}') && (
            <p className="text-xs text-amber-600 font-medium mt-1">
              ⚠️ Format must include {'{NUMBER}'} (e.g. DT-{'{PREFIX}'}-{'{FY}'}/{'{NUMBER}'}) so document numbers increment automatically.
            </p>
          )}
        </div>

        <div className="flex items-center gap-4 p-3 bg-muted/50 rounded-lg">
          <div className="flex items-center gap-2">
            <Label className="text-sm font-medium">Preview:</Label>
            <span className="font-mono text-lg bg-background px-3 py-1 rounded border">{previewNumber(config)}</span>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => resetCounter(setDraft, docType)}
            disabled={config.currentCounter === 0}
          >
            Reset Counter
          </Button>
        </div>

      </div>
    )
  }

  return (
    <div className="space-y-4 max-w-4xl">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold text-foreground">Document Numbering</h2>
          <p className="text-sm text-muted-foreground">Manage document numbering sequences for all sales documents.</p>
        </div>
        <Button onClick={openCreate} className="gap-1.5">
          <Plus className="h-4 w-4" /> New Configuration
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-4">
            {/* Search */}
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search configurations..."
                value={search}
                onChange={(e) => handleSearchChange(e.target.value)}
                className="pl-9"
              />
            </div>

            {/* Active Filter */}
            <Select value={isActiveFilter} onValueChange={handleIsActiveChange}>
              <SelectTrigger className="w-[140px]">
                <SelectValue placeholder="All Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="true">Active</SelectItem>
                <SelectItem value="false">Inactive</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : configs.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <p className="text-sm text-muted-foreground">
              No numbering configurations yet. Create the first one.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {configs.map((item) => (
            <Card key={item.id}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-3 min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <Hash className="h-5 w-5 text-muted-foreground" />
                      <Badge variant={item.isActive ? 'default' : 'secondary'} className="text-xs">
                        {item.isActive ? 'Active' : 'Inactive'}
                      </Badge>
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-semibold text-foreground truncate">{item.name}</p>
                      </div>
                      <p className="text-xs text-muted-foreground truncate flex items-center gap-2">
                        {DOC_TYPES.map((dt) => {
                          const config = item[dt.key]
                          if (!config || !config.isActive) return null
                          return (
                            <span key={dt.key} className="font-mono text-[10px] px-1.5 py-0.5 bg-muted rounded">
                              {dt.prefix}: {config.numberFormat
                                .replace('{PREFIX}', config.prefix)
                                .replace('{FY}', normalizeFiscalYear(config.fiscalYear))
                                .replace('{NUMBER}', (config.currentCounter + 1).toString().padStart(config.padding, '0'))}
                            </span>
                          )
                        })}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <Button variant="ghost" size="icon" onClick={() => openEdit(item)} title="Edit">
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleToggleActive(item)}
                      title={item.isActive ? 'Deactivate' : 'Activate'}
                    >
                      {item.isActive ? (
                        <span className="h-3.5 w-3.5 rounded-full bg-green-500" />
                      ) : (
                        <span className="h-3.5 w-3.5 rounded-full bg-gray-400" />
                      )}
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDelete(item)}
                      disabled={deletingId === item.id}
                      title="Delete"
                    >
                      <Trash2 className="h-3.5 w-3.5 text-destructive" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-3xl">
          <DialogHeader>
            <DialogTitle>{draft.id ? 'Edit Document Numbering' : 'New Document Numbering Configuration'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2 max-h-[80vh] overflow-y-auto">
            <div className="space-y-1.5">
              <Label htmlFor="dn-name">Configuration Name *</Label>
              <Input
                id="dn-name"
                value={draft.name}
                onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
                placeholder="e.g. Standard FY 2024-25"
              />
            </div>

            <div className="flex border-b border-border">
              {DOC_TYPES.map((docType) => (
                <button
                  key={docType.key}
                  onClick={() => setActiveTab(docType.key)}
                  className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
                    activeTab === docType.key
                      ? 'border-primary text-primary font-semibold'
                      : 'border-transparent text-muted-foreground hover:text-foreground'
                  }`}
                >
                  <docType.icon className="h-4 w-4" />
                  {docType.label}
                </button>
              ))}
            </div>

            {renderConfigFields(activeTab)}
          </div>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={create.isPending || update.isPending}>
              {draft.id ? 'Save Changes' : 'Create Configuration'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}